import { useState, useRef, useEffect } from "react";
import api, { apiErr } from "@/lib/api";
import { Sparkles, Send } from "lucide-react";

const SID = "cara-" + Math.random().toString(36).slice(2, 10);

export default function CaraChat() {
  const [messages, setMessages] = useState([
    { id: "m0", role: "assistant", content: "Hi! I'm Cara. Not sure what kind of carer you need? Tell me about who needs care and I'll suggest the right service and shortlist verified carers." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const history = messages.slice(1).map(({ role, content }) => ({ role, content }));
    setMessages((m) => [...m, { id: `u${m.length}`, role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/cara/chat", { session_id: SID, message: text, history });
      setMessages((m) => [...m, { id: `a${m.length}`, role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { id: `a${m.length}`, role: "assistant", content: "Sorry, I'm having trouble right now. " + apiErr(e) }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card-bmc overflow-hidden flex flex-col h-[440px] shadow-bmc-lg" data-testid="cara-chat">
      <div className="flex items-center gap-3 p-4 border-b border-line-2 bg-ivory-2">
        <span className="relative grid place-items-center w-11 h-11 rounded-full text-white" style={{ background: "linear-gradient(140deg,#EC5E3F,#F4C45D)" }}>
          <Sparkles size={18} />
          <span className="absolute inset-0 rounded-full bg-coral animate-pulse-ring" />
        </span>
        <div>
          <p className="font-display font-bold text-ink flex items-center gap-2">Cara <span className="text-[10px] bg-coral text-white px-2 py-0.5 rounded-full">New</span></p>
          <p className="text-xs text-ink-soft">Your AI care concierge</p>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 no-scrollbar">
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] text-sm rounded-2xl px-3.5 py-2.5 ${m.role === "user" ? "bg-coral text-white rounded-br-sm" : "bg-ivory-2 text-ink rounded-bl-sm"}`}>
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-ivory-2 rounded-2xl px-4 py-3 flex gap-1">
              {[0, 1, 2].map((d) => <span key={d} className="w-1.5 h-1.5 rounded-full bg-ink-faint animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />)}
            </div>
          </div>
        )}
      </div>

      <div className="p-3 border-t border-line-2 flex gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Tell Cara who needs care..." className="flex-1 bg-ivory rounded-full px-4 py-2.5 text-sm focus:outline-none border border-line focus:border-coral" data-testid="cara-input" />
        <button onClick={send} disabled={loading} className="btn-pill bg-coral text-white w-11 h-11 justify-center disabled:opacity-60" data-testid="cara-send"><Send size={16} /></button>
      </div>
    </div>
  );
}
